// Single source of truth for the curation review pane. Scores are
// never stored here — they are derived on render by curationScoring's pure
// functions, so every override recalculates instantly and for free.

import { ServerReviewState } from '../utils/curationApi';
import {
  AiPayload,
  DirectOverrides,
  GateCheck,
  GateResult,
  nextSkillState,
  SkillState,
} from '../utils/curationScoring';

export type AiStatus = 'locked' | 'loading' | 'loaded' | 'error';

export interface DecisionState {
  candidateMessage: string;
  internalNote: string;
  reason: string | null;
  reasonOther: string;
  type: 'approve' | 'reject' | null;
}

export interface ReviewState {
  aiError: string | null;
  aiStatus: AiStatus;
  decision: DecisionState;
  directOverrides: DirectOverrides;
  gateChecks: GateCheck[];
  gateConfirmed: boolean;
  handpicked: boolean;
  notes: string;
  payload: AiPayload | null;
  skillStates: Record<string, SkillState>;
  submitError: string | null;
  submitStatus: 'idle' | 'submitting' | 'error';
  submitted: boolean;
}

export type ReviewAction =
  | { type: 'OVERRIDE_GATE_CHECK'; key: string; value: GateResult | null }
  | { type: 'CONFIRM_GATE' }
  | { type: 'REOPEN_GATE' }
  | { type: 'AI_LOADING' }
  | { type: 'AI_LOADED'; payload: AiPayload }
  | { type: 'AI_FAILED'; error: string }
  | { type: 'TOGGLE_SKILL'; name: string; from: SkillState }
  | { type: 'RESET_SKILL'; name: string; state?: SkillState }
  | { type: 'SET_DIRECT_OVERRIDE'; key: keyof DirectOverrides; value: number | null }
  | { type: 'SET_NOTES'; value: string }
  | { type: 'SET_HANDPICKED'; value: boolean }
  | { type: 'SET_DECISION'; value: Partial<DecisionState> }
  | { type: 'SUBMIT_START' }
  | { type: 'SUBMIT_FAILED'; error: string }
  | { type: 'SUBMITTED' };

const aiStatusFor = (server: ServerReviewState): AiStatus => {
  switch (server.status) {
    case 'scoring':
      return 'loading';
    case 'scored':
      return 'loaded';
    case 'scoring_failed':
      return 'error';
    case 'submitted':
      return server.ai.payload ? 'loaded' : 'locked';
    default:
      return 'locked';
  }
};

export const initReviewState = (server: ServerReviewState): ReviewState => ({
  aiError: server.ai.error,
  aiStatus: aiStatusFor(server),
  decision: {
    candidateMessage: '',
    internalNote: '',
    reason: server.suggestedRejectionReason,
    reasonOther: '',
    type: null,
  },
  directOverrides: {
    context_pct: server.overrides.direct.context_pct ?? null,
    skills_pct: server.overrides.direct.skills_pct ?? null,
    years_pct: server.overrides.direct.years_pct ?? null,
  },
  gateChecks: server.gate.checks,
  gateConfirmed: server.status !== 'created',
  handpicked: false,
  notes: '',
  payload: server.ai.payload,
  skillStates: server.overrides.skills || {},
  submitError: null,
  submitStatus: 'idle',
  submitted: server.status === 'submitted',
});

export const curationReviewReducer = (state: ReviewState, action: ReviewAction): ReviewState => {
  switch (action.type) {
    case 'OVERRIDE_GATE_CHECK':
      return {
        ...state,
        gateChecks: state.gateChecks.map((check) =>
          check.key === action.key ? { ...check, override: action.value } : check,
        ),
      };
    case 'CONFIRM_GATE':
      return { ...state, gateConfirmed: true };
    case 'REOPEN_GATE':
      return { ...state, gateConfirmed: false };
    case 'AI_LOADING':
      return { ...state, aiError: null, aiStatus: 'loading' };
    case 'AI_LOADED': {
      return { ...state, aiStatus: 'loaded', payload: action.payload };
    }
    case 'AI_FAILED':
      return { ...state, aiError: action.error, aiStatus: 'error' };
    case 'TOGGLE_SKILL': {
      const current = state.skillStates[action.name] || action.from;
      return {
        ...state,
        skillStates: { ...state.skillStates, [action.name]: nextSkillState(current) },
      };
    }
    case 'RESET_SKILL': {
      const skillStates = { ...state.skillStates };
      if (action.state === undefined) {
        delete skillStates[action.name];
      } else {
        skillStates[action.name] = action.state;
      }
      return { ...state, skillStates };
    }
    case 'SET_DIRECT_OVERRIDE':
      return {
        ...state,
        directOverrides: { ...state.directOverrides, [action.key]: action.value },
      };
    case 'SET_NOTES':
      return { ...state, notes: action.value };
    case 'SET_HANDPICKED':
      return { ...state, handpicked: action.value };
    case 'SET_DECISION':
      return { ...state, decision: { ...state.decision, ...action.value } };
    case 'SUBMIT_START':
      return { ...state, submitError: null, submitStatus: 'submitting' };
    case 'SUBMIT_FAILED':
      return { ...state, submitError: action.error, submitStatus: 'error' };
    case 'SUBMITTED':
      return { ...state, submitStatus: 'idle', submitted: true };
    default:
      return state;
  }
};
